import { useEffect, useRef, useState } from 'react'

import { apiService } from '@/service/api'
import type { GraphReadinessState } from '@/types/graph'

/**
 * FS-621 — poll GET /v1/graph/:graphId/status while a P&ID is processing.
 *
 * The graph editor used to do a one-shot fetch and, on a not-found/empty/5xx
 * response, show "still being processed" forever with no retry. This hook polls
 * the readiness endpoint with bounded exponential backoff and STOPS on any
 * terminal state:
 *   - `ready`     → the graph can be (re)loaded; `onReady` fires.
 *   - `failed`    → surface `errorMessage` + a retry affordance.
 *   - `not_found` → a genuinely unknown id; stop (don't spin on a bad link).
 * Only `pending` keeps polling, and only up to `maxAttempts` so a wedged
 * backend can't make the hook poll indefinitely (after which `polling` flips
 * false while the last state — typically `pending` — is retained, letting the
 * UI show "taking longer than expected").
 */
export type GraphPollState = GraphReadinessState | 'unknown'

export interface UseGraphStatusPollOptions {
  /** Poll only while true (e.g. while the editor couldn't load the graph). */
  enabled?: boolean
  /** Base for exponential backoff (ms). The first poll fires immediately; the
   *  second fires after ~1.5× this value. */
  intervalMs?: number
  /** Backoff cap (ms). */
  maxIntervalMs?: number
  /** Stop after this many polls while still pending. */
  maxAttempts?: number
  /** Fired once when the graph transitions to `ready`. */
  onReady?: () => void
}

export interface UseGraphStatusPollResult {
  state: GraphPollState
  errorMessage?: string
  /** True while actively polling (not yet terminal / not yet exhausted). */
  polling: boolean
}

const TERMINAL: ReadonlySet<GraphReadinessState> = new Set([
  'ready',
  'failed',
  'not_found',
])

export function useGraphStatusPoll(
  graphId: string | null | undefined,
  options: UseGraphStatusPollOptions = {}
): UseGraphStatusPollResult {
  const {
    enabled = true,
    intervalMs = 2000,
    maxIntervalMs = 15000,
    maxAttempts = 40,
    onReady,
  } = options

  const [state, setState] = useState<GraphPollState>('unknown')
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [polling, setPolling] = useState(false)

  // Keep onReady current without re-subscribing the poll loop on every render.
  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady

  useEffect(() => {
    if (!enabled || !graphId) {
      setPolling(false)
      return
    }

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let attempts = 0
    let delay = intervalMs
    const controller = new AbortController()

    setPolling(true)
    setState('unknown')
    setErrorMessage(undefined)

    const poll = async () => {
      attempts += 1
      try {
        const res = await apiService.getGraphStatus(graphId, {
          signal: controller.signal,
        })
        if (cancelled) return
        setState(res.state)
        // Only `failed` carries an error message; clearing it otherwise avoids
        // a stale message lingering if the backend ever returns one on success.
        setErrorMessage(res.state === 'failed' ? res.error_message : undefined)
        if (TERMINAL.has(res.state)) {
          setPolling(false)
          if (res.state === 'ready') onReadyRef.current?.()
          return
        }
      } catch (err) {
        if (cancelled || controller.signal.aborted) return
        // A permanent client error (401/403/404, etc.) won't resolve by
        // retrying — stop immediately rather than poll for the full
        // maxAttempts window. Only network blips / 5xx are treated as
        // transient and keep polling.
        const status = (err as { response?: { status?: number } })?.response?.status
        if (typeof status === 'number' && status >= 400 && status < 500) {
          setPolling(false)
          return
        }
        // else: transient — fall through and schedule another attempt.
      }
      if (cancelled) return
      if (attempts >= maxAttempts) {
        // Give up; retain the last state (usually 'pending') so the UI can show
        // "taking longer than expected" rather than a hard error.
        setPolling(false)
        return
      }
      delay = Math.min(Math.round(delay * 1.5), maxIntervalMs)
      timer = setTimeout(poll, delay)
    }

    // Poll once immediately, then back off.
    void poll()

    return () => {
      cancelled = true
      controller.abort()
      if (timer) clearTimeout(timer)
    }
  }, [graphId, enabled, intervalMs, maxIntervalMs, maxAttempts])

  return { state, errorMessage, polling }
}
